/**
 * Stage State Machine
 * Manages stage transitions, step progression, and stage-specific logic
 * Based on game_model.yaml stages section
 */

import {
  GameState,
  StageId,
  STAGES,
  STAGE_5_QUESTIONS,
  StageStep
} from '../models/GameState';
import { QuestionId } from '../models/Question';
import { removeItem } from '../models/Inventory';

export class StageManager {
  /**
   * Get current stage information
   */
  getCurrentStage(gameState: GameState) {
    return STAGES[gameState.currentStage];
  }

  /**
   * Get current step information
   */
  getCurrentStep(gameState: GameState): StageStep | undefined {
    const stage = this.getCurrentStage(gameState);
    return stage.steps.find(s => s.step === gameState.currentStep);
  }

  /**
   * Get questions for current step
   */
  getCurrentQuestions(gameState: GameState): QuestionId[] {
    const step = this.getCurrentStep(gameState);
    if (!step) return [];

    // Special handling for Stage 5 - questions depend on variant
    if (gameState.currentStage === 5) {
      const variant = gameState.flags.stage5Variant;
      return STAGE_5_QUESTIONS[variant];
    }

    return step.questions;
  }

  /**
   * Advance to next step within current stage
   */
  advanceStep(gameState: GameState): GameState {
    const stage = this.getCurrentStage(gameState);
    const currentStepIndex = stage.steps.findIndex(s => s.step === gameState.currentStep);

    if (currentStepIndex === -1) {
      throw new Error(`Current step ${gameState.currentStep} not found in stage ${gameState.currentStage}`);
    }

    // Check if there's a next step
    if (currentStepIndex < stage.steps.length - 1) {
      const nextStep = stage.steps[currentStepIndex + 1];
      return {
        ...gameState,
        currentStep: nextStep.step,
        updatedAt: new Date()
      };
    }

    // No more steps - ready to advance stage
    return gameState;
  }

  /**
   * Check if current stage is complete
   */
  isStageComplete(gameState: GameState): boolean {
    const stage = this.getCurrentStage(gameState);
    const currentStepIndex = stage.steps.findIndex(s => s.step === gameState.currentStep);
    return currentStepIndex === stage.steps.length - 1;
  }

  /**
   * Determine Stage 5 variant based on Fixer's Q7 answer
   */
  determineStage5Variant(gameState: GameState): 'A' | 'B' {
    // Find Fixer player
    const fixer = gameState.players.find(p => p.role === 'FIXER');
    if (!fixer) {
      console.warn('Fixer not found, defaulting to Stage 5A');
      return 'A';
    }

    // Get Fixer's Q7 answer
    const q7Answer = fixer.decisions.get('Q7');

    // Stage 5B if Fixer chose B (Ship), otherwise 5A
    // Per game_model.yaml: trigger: "FIXER.Q7 == 'B'"
    return q7Answer === 'B' ? 'B' : 'A';
  }

  /**
   * Apply stage entry effects
   */
  applyStageEntryEffects(gameState: GameState, stageId: StageId): GameState {
    let newState = { ...gameState };

    switch (stageId) {
      case 1:
        // Stage 1: Storm causes loss of 2 fuel canisters
        newState.inventory = removeItem(newState.inventory, 'fuel_canisters', 2);

        // Container lost in storm
        newState.flags = {
          ...newState.flags,
          containerLost: true
        };
        break;

      case 2:
        // Stage 2: Decompression chamber set up in cave
        newState.inventory = removeItem(newState.inventory, 'decompression_chamber', 1);
        break;

      case 5:
        // Stage 5: Determine variant
        const variant = this.determineStage5Variant(newState);
        newState.flags = {
          ...newState.flags,
          stage5Variant: variant
        };
        newState.currentVariant = variant;
        break;
    }

    return newState;
  }

  /**
   * Advance to next stage
   */
  advanceStage(gameState: GameState): GameState {
    if (gameState.currentStage >= 5) {
      // Already at final stage
      return {
        ...gameState,
        gameStatus: 'completed',
        updatedAt: new Date()
      };
    }

    const nextStageId = (gameState.currentStage + 1) as StageId;
    const nextStage = STAGES[nextStageId];

    if (!nextStage) {
      throw new Error(`Next stage ${nextStageId} not found`);
    }

    // Apply stage entry effects
    let newState = this.applyStageEntryEffects(gameState, nextStageId);

    // Update to next stage
    newState = {
      ...newState,
      currentStage: nextStageId,
      currentStep: nextStage.steps[0].step,
      updatedAt: new Date()
    };

    return newState;
  }

  /**
   * Calculate current hunting permit day
   * Formula from game_model.yaml: base_day + Q5 delay + Q6 delay
   */
  calculateHuntingPermitDay(gameState: GameState): number {
    let day = gameState.currentStage;

    // Check Q5 answer (departure timing)
    const q5Decision = gameState.decisions.find(d => d.questionId === 'Q5');
    if (q5Decision?.finalAnswer === 'B') {
      day += 1; // Waited until first light
    }

    // Check Q6 answer (send GEE back)
    const q6Decision = gameState.decisions.find(d => d.questionId === 'Q6');
    if (q6Decision?.finalAnswer === 'A' || q6Decision?.finalAnswer === 'B') {
      day += 1; // Sending GEE back loses a day
    }

    return Math.min(day, 6); // Max 6 days
  }

  /**
   * Update hunting permit day
   */
  updateHuntingPermitDay(gameState: GameState): GameState {
    return {
      ...gameState,
      huntingPermitDay: this.calculateHuntingPermitDay(gameState)
    };
  }

  /**
   * Check if game should end (permit expired)
   */
  isPermitExpired(gameState: GameState): boolean {
    return gameState.huntingPermitDay > 6;
  }

  /**
   * Get stage progress (0-100%)
   */
  getStageProgress(gameState: GameState): number {
    const stage = this.getCurrentStage(gameState);
    const currentStepIndex = stage.steps.findIndex(s => s.step === gameState.currentStep);

    if (currentStepIndex === -1) return 0;

    return Math.round(((currentStepIndex + 1) / stage.steps.length) * 100);
  }

  /**
   * Get overall game progress (0-100%)
   */
  getGameProgress(gameState: GameState): number {
    if (gameState.gameStatus === 'completed' || gameState.gameStatus === 'failed') {
      return 100;
    }
    const stageProgress = gameState.currentStage / 5; // 0-1 for stages
    const stepProgress = this.getStageProgress(gameState) / 100 / 5; // Contribution of current step

    return Math.min(100, Math.round((stageProgress + stepProgress) * 100));
  }

  /**
   * Validate stage transition
   */
  canAdvanceStage(gameState: GameState): boolean {
    // Check if current stage is complete
    if (!this.isStageComplete(gameState)) {
      return false;
    }

    // Check if already at final stage
    if (gameState.currentStage >= 5) {
      return false;
    }

    return true;
  }

  /**
   * Validate step transition
   */
  canAdvanceStep(gameState: GameState): boolean {
    const stage = this.getCurrentStage(gameState);
    const currentStepIndex = stage.steps.findIndex(s => s.step === gameState.currentStep);

    if (currentStepIndex === -1) {
      return false;
    }

    // Check if there's a next step
    return currentStepIndex < stage.steps.length - 1;
  }
}

// Singleton instance
export const stageManager = new StageManager();
